import { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from './icons';
import { LANGS, loadLang, useLanguage } from '../i18n';

/**
 * The language switch in the header: five languages, English first. Only
 * the interface is translated — event names, descriptions and blog posts
 * stay English — so the menu says so in one line.
 */
const LanguageMenu = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  // The list is open: download the dictionaries while the visitor reads it (each once; English has
  // none), so the pick switches at once instead of waiting for its chunk (src/i18n loadLang).
  useEffect(() => {
    if (open) for (const l of LANGS) if (l.code !== 'en') void loadLang(l.code);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="relative inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-[13.5px] font-medium uppercase tracking-[0.03em] text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground max-md:after:absolute max-md:after:-inset-y-1"
      >
        <span className="sr-only">{t('Language')}: </span>
        {current.code}
        <ChevronDownIcon className="size-3.5 opacity-70" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('Language')}
          className="absolute right-0 top-full z-30 mt-1.5 w-[232px] overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-lg"
        >
          {LANGS.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                lang={l.code}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-3 px-3 py-3 text-left md:py-2 text-[13.5px] hover:bg-muted"
              >
                <span className="w-7 shrink-0 uppercase text-muted-foreground">{l.code}</span>
                <span className="min-w-0 flex-1 font-medium">{l.name}</span>
                {active && <CheckIcon className="size-4 shrink-0 text-primary" />}
              </button>
            );
          })}
          <p className="mt-1 border-t border-border px-3 pb-1.5 pt-2 text-[11.5px] leading-relaxed text-muted-foreground">
            {t('Menus and labels are translated; event names, descriptions and articles stay in English.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default LanguageMenu;
